
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FileSpreadsheet, FileText, Image } from "lucide-react";
import { Event } from "@/types/event";
import { useToast } from "@/hooks/use-toast";

interface ExportManagerProps {
  event: Event;
}

interface ExportOptions {
  format: 'csv' | 'json' | 'pdf' | 'xlsx';
  includeVotes: boolean;
  includeGuests: boolean;
  includeComments: boolean;
  includeAnalytics: boolean;
}

export const ExportManager = ({ event }: ExportManagerProps) => {
  const { toast } = useToast();
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'csv',
    includeVotes: true,
    includeGuests: true,
    includeComments: false,
    includeAnalytics: false,
  });
  const [exporting, setExporting] = useState(false);

  const generateCSV = () => {
    const data = [];
    
    // Event info
    data.push(['Event Information']);
    data.push(['Name', event.name]);
    data.push(['Description', event.description]);
    data.push(['Host', event.hostName]);
    data.push(['Status', event.isActive ? 'Active' : 'Inactive']);
    data.push(['Total Voters', event.totalVoters]);
    data.push(['']);

    // Options and votes
    if (exportOptions.includeVotes) {
      data.push(['Voting Results']);
      data.push(['Option Type', 'Option Value', 'Votes', 'Percentage']);
      
      event.options.forEach(option => {
        const percentage = event.totalVoters > 0 ? 
          Math.round((option.votes / event.totalVoters) * 100) : 0;
        data.push([
          option.type,
          formatOptionValue(option),
          option.votes,
          `${percentage}%`
        ]);
      });
      data.push(['']);
    }

    // Guests
    if (exportOptions.includeGuests) {
      data.push(['Guest List']);
      data.push(['Name', 'Email', 'Phone', 'Has Voted', 'Invited At']);
      
      event.guests.forEach(guest => {
        data.push([
          guest.name || '',
          guest.email || '',
          guest.phone || '',
          guest.hasVoted ? 'Yes' : 'No',
          new Date(guest.invitedAt).toLocaleDateString()
        ]);
      });
    }

    return data.map(row => row.join(',')).join('\n');
  };

  const generateJSON = () => {
    const exportData: any = {
      event: {
        id: event.id,
        name: event.name,
        description: event.description,
        hostName: event.hostName,
        isActive: event.isActive,
        totalVoters: event.totalVoters,
        shareableLink: event.shareableLink,
      }
    };

    if (exportOptions.includeVotes) {
      exportData.votingResults = event.options.map(option => ({
        type: option.type,
        value: formatOptionValue(option),
        votes: option.votes,
        percentage: event.totalVoters > 0 ? 
          Math.round((option.votes / event.totalVoters) * 100) : 0,
        voters: option.voters,
      }));
    }

    if (exportOptions.includeGuests) {
      exportData.guests = event.guests.map(guest => ({
        name: guest.name,
        email: guest.email,
        phone: guest.phone,
        hasVoted: guest.hasVoted,
        invitedAt: guest.invitedAt,
      }));
    }

    return JSON.stringify(exportData, null, 2);
  };

  const formatOptionValue = (option: any) => {
    switch (option.type) {
      case 'date':
        return new Date(option.value).toLocaleDateString();
      case 'time':
        return new Date(`2000-01-01T${option.value}`).toLocaleTimeString([], { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case 'location':
        return option.value;
      default:
        return option.value;
    }
  };

  const handleExport = async () => {
    setExporting(true);
    
    try {
      let content: string;
      let mimeType: string;
      let fileExtension: string;

      switch (exportOptions.format) {
        case 'csv':
          content = generateCSV();
          mimeType = 'text/csv;charset=utf-8';
          fileExtension = 'csv';
          break;
        case 'json':
          content = generateJSON();
          mimeType = 'application/json;charset=utf-8';
          fileExtension = 'json';
          break;
        default:
          throw new Error('Export format not yet implemented');
      }

      const blob = new Blob([content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${event.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.${fileExtension}`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Export complete",
        description: `Event data has been exported as ${exportOptions.format.toUpperCase()} file.`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export failed",
        description: "Failed to export event data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  const getFormatIcon = (format: string) => {
    switch (format) {
      case 'csv':
      case 'xlsx':
        return <FileSpreadsheet className="w-4 h-4" />;
      case 'json':
        return <FileText className="w-4 h-4" />;
      case 'pdf':
        return <Image className="w-4 h-4" />;
      default:
        return <Download className="w-4 h-4" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="w-5 h-5" />
          Export Event Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Export Format</label>
            <Select 
              value={exportOptions.format} 
              onValueChange={(value: any) => setExportOptions(prev => ({ ...prev, format: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV (Spreadsheet)</SelectItem>
                <SelectItem value="json">JSON (Data)</SelectItem>
                <SelectItem value="xlsx" disabled>Excel (Coming Soon)</SelectItem>
                <SelectItem value="pdf" disabled>PDF (Coming Soon)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium">Include in Export</label>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="includeVotes"
                checked={exportOptions.includeVotes}
                onCheckedChange={(checked) => 
                  setExportOptions(prev => ({ ...prev, includeVotes: !!checked }))
                }
              />
              <label htmlFor="includeVotes" className="text-sm">Voting results</label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="includeGuests"
                checked={exportOptions.includeGuests}
                onCheckedChange={(checked) => 
                  setExportOptions(prev => ({ ...prev, includeGuests: !!checked }))
                }
              />
              <label htmlFor="includeGuests" className="text-sm">Guest list</label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="includeComments"
                checked={exportOptions.includeComments}
                onCheckedChange={(checked) => 
                  setExportOptions(prev => ({ ...prev, includeComments: !!checked }))
                }
                disabled
              />
              <label htmlFor="includeComments" className="text-sm text-muted-foreground">
                Comments (Coming Soon)
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="includeAnalytics"
                checked={exportOptions.includeAnalytics}
                onCheckedChange={(checked) => 
                  setExportOptions(prev => ({ ...prev, includeAnalytics: !!checked }))
                }
                disabled
              />
              <label htmlFor="includeAnalytics" className="text-sm text-muted-foreground">
                Analytics data (Coming Soon)
              </label>
            </div>
          </div>

          <Button 
            onClick={handleExport}
            disabled={exporting || (!exportOptions.includeVotes && !exportOptions.includeGuests)}
            className="w-full"
          >
            {getFormatIcon(exportOptions.format)}
            <span className="ml-2">
              {exporting ? 'Exporting...' : `Export as ${exportOptions.format.toUpperCase()}`}
            </span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
